/**
 * Thin admin PWA analytics ingest — mirrors customer session start + emit.
 * Best-effort: never throws. Uses tenant from URL + apiClient auth token.
 */
import { parseAnalyticsStartSessionData } from 'pi-kiosk-shared/analyticsApiTypes';
import {
  ANALYTICS_EVENT_CATALOG_VERSION,
  ANALYTICS_PWA_EVENTS,
  ANALYTICS_TELEMETRY_CLASSES,
} from 'pi-kiosk-shared/analyticsEvents';
import { apiClient } from '../../services/api/apiClient.js';
import { getTenantFromPath } from '../../shared/tenant.js';
import { appLogger } from '../../shared/logging/appLogger.js';

const ANALYTICS_SESSION_AUTH_HEADER = 'X-Analytics-Session-Auth';

export type AdminPwaAnalyticsEventName =
  (typeof ANALYTICS_PWA_EVENTS)[keyof typeof ANALYTICS_PWA_EVENTS];

export interface AdminPwaAnalyticsPayload {
  readonly eventName: AdminPwaAnalyticsEventName;
  readonly screenName?: string;
  readonly metadata?: {
    readonly outcome?: string;
    readonly version?: string;
    /** tenant = Admin PWA; dev = /dev Dev PWA */
    readonly pwaSurface?: 'tenant' | 'dev';
  };
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readBuildVersion(): string | undefined {
  const raw = import.meta.env.VITE_BUILD_VERSION;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : undefined;
}

let sessionId: string | null = null;
let sessionAuthToken: string | null = null;
let startPromise: Promise<string> | null = null;

function resetSessionState(): void {
  sessionId = null;
  sessionAuthToken = null;
  startPromise = null;
}

async function startSession(): Promise<string> {
  const tenantCode = getTenantFromPath();
  if (tenantCode === null) {
    throw new Error('admin_pwa_analytics_no_tenant');
  }

  const response = await apiClient.post<unknown>('/api/v1/analytics/sessions', {
    sessionId: uuidv4(),
    salesPointId: null,
    platform: 'MOBILE',
    flow: null,
    anonymousKey: null,
    metadata: {},
  });

  const parsed = parseAnalyticsStartSessionData(response.data);
  if (parsed.isClosed) {
    resetSessionState();
    const retry = await apiClient.post<unknown>('/api/v1/analytics/sessions', {
      sessionId: uuidv4(),
      salesPointId: null,
      platform: 'MOBILE',
      flow: null,
      anonymousKey: null,
      metadata: {},
    });
    const retried = parseAnalyticsStartSessionData(retry.data);
    sessionId = retried.sessionId;
    sessionAuthToken = retried.sessionAuthToken ?? null;
    return retried.sessionId;
  }

  sessionId = parsed.sessionId;
  sessionAuthToken = parsed.sessionAuthToken ?? null;
  return parsed.sessionId;
}

async function ensureSession(): Promise<string> {
  if (sessionId !== null) {
    return sessionId;
  }
  if (startPromise !== null) {
    return startPromise;
  }
  startPromise = startSession();
  try {
    return await startPromise;
  } finally {
    startPromise = null;
  }
}

function buildMetadata(
  metadata: AdminPwaAnalyticsPayload['metadata'],
): Record<string, unknown> {
  const version = metadata?.version ?? readBuildVersion();
  const out: Record<string, unknown> = {};
  if (metadata?.outcome !== undefined) {
    out.outcome = metadata.outcome;
  }
  if (metadata?.pwaSurface !== undefined) {
    out.pwaSurface = metadata.pwaSurface;
  }
  if (version !== undefined) {
    out.version = version;
  }
  return out;
}

/**
 * Fire-and-forget ingest: lazy POST /analytics/sessions then POST /analytics/events.
 * Swallows all errors so PWA UI never breaks.
 */
export async function trackAdminPwaAnalytics(
  payload: AdminPwaAnalyticsPayload,
): Promise<void> {
  try {
    const currentSessionId = await ensureSession();
    const headers: Record<string, string> =
      sessionAuthToken !== null
        ? { [ANALYTICS_SESSION_AUTH_HEADER]: sessionAuthToken }
        : {};

    await apiClient.post(
      '/api/v1/analytics/events',
      {
        sessionId: currentSessionId,
        eventName: payload.eventName,
        catalogVersion: ANALYTICS_EVENT_CATALOG_VERSION,
        clientEventId: uuidv4(),
        paymentId: null,
        transactionId: null,
        flow: null,
        telemetryClass: ANALYTICS_TELEMETRY_CLASSES.OPERATIONAL,
        anonymousKey: null,
        metadata: buildMetadata(payload.metadata),
        screenName: payload.screenName ?? 'pwa_lifecycle',
        failureReason: null,
      },
      false,
      { kind: 'default' },
      { headers },
    );
  } catch (err) {
    resetSessionState();
    appLogger.warn('admin PWA analytics ingest failed', { error: err });
  }
}

/** Test / logout hook — clears in-memory session auth. */
export function resetAdminPwaAnalyticsSession(): void {
  resetSessionState();
}
